import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import "react-calendar/dist/Calendar.css";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { ToastText } from "./FxryToaster";
import { useNavigate } from "react-router-dom";
import useCartStore from "../api/cart";
import { withoutService } from "../utils/cartSummary";
import {
  loadCheckoutAnswers,
  saveCheckoutAnswers,
} from "../utils/checkoutAnswers";
import btnArrow from "../assets/img/section-btn-arrow.webp";
import CheckoutForm from "./CheckoutForm";
import TerminalCheckoutForm from "./TerminalCheckoutForm";
import { authHeader } from "../utils/staffAuth";
import Loader from "./Loader";
import { loadStaffPrefs, saveStaffPrefs } from "../utils/staffPrefs";
import { loadPrefill } from "../utils/appointmentPrefill";
import CustomerLookup from "./CustomerLookup";
import {
  centsToDollars,
  chargedWithGiftSale,
  giftPayloadFields,
  giftSaleAmountCents,
  giftSaleFields,
  giftSaleRefusal,
  giftSplit,
} from "../utils/giftCertificate";
import {
  customLineKey,
  displayFromQuote,
  isServerPricingOn,
  linesNeedingReason,
  missingReasons,
  popupDeliveryFees,
  pricesChangedNotice,
  pricingRefusal,
  quotePayload,
  reasonFields,
  onUsEntry,
  shipBackFeeFrom,
  withCustomReasons,
  withOnUs,
} from "../utils/serverPricing";
const pathURL = process.env.REACT_APP_API_URL;

/** Same studio address as fxryshop drop-off tax (Stripe Tax via order/calculate-tax). */
const STUDIO_TAX_ADDRESS = {
  city: "Seattle",
  state: "WA",
  zip: "98144",
  zipcode: "98144",
  country: "US",
};

/** Percentage discounts apply to services + all fees (pickup, rush, mail-out). 100% also waives tax. */
/** Round a currency value to two decimal places (cents). */
const roundToCents = (value) =>
  Math.round((Number(value) + Number.EPSILON) * 100) / 100;

const computePopupPromoDiscountAmount = (
  meta,
  servicesSubtotal,
  pickupFee,
  taxAmount,
) => {
  if (!meta) return 0;
  const services = Number(servicesSubtotal) || 0;
  const fees = Number(pickupFee) || 0;
  const tax = Number(taxAmount) || 0;
  const grandBeforeDiscount = services + fees + tax;

  if (meta.type === "fixed") {
    const amount = parseFloat(meta.fixedAmount);
    if (!Number.isFinite(amount) || amount <= 0) return 0;
    return roundToCents(Math.min(amount, grandBeforeDiscount));
  }

  if (meta.type === "percentage") {
    const pct = parseFloat(meta.percent);
    if (!Number.isFinite(pct) || pct <= 0) return 0;
    const base = services + fees;
    if (pct >= 100) {
      return roundToCents(grandBeforeDiscount);
    }
    return roundToCents(Math.min((pct / 100) * base, grandBeforeDiscount));
  }

  return 0;
};

const INTAKE_PEOPLE = ["Rebecca", "Calley", "Natalie"];

/** Form defaults from the check-in choices remembered on this device. A stored
 *  name that is no longer an option is ignored rather than left on a select
 *  that can't show it. */
// Keep in the same order as fxryshop's Checkout.jsx so in-store and online
// answers can be reported together.
const HEAR_ABOUT_US_OPTIONS = [
  "Local Shop Referral",
  "Word Of Mouth",
  "Neighborhood / Walked By",
  "Facebook",
  "Instagram",
  "TikTok",
  "Online Search",
  "AI Search (ChatGPT, Claude, etc.)",
  "Online Ad",
  "Pop-Up Event",
  "I've Used FXRY Before",
  "Other",
];

/** The booking page's answer, split the way this form holds it:
 *  "Local Shop Referral - Blue Owl" → the option plus the shop. Anything not
 *  on the list is dropped, so staff ask as usual. */
const bookedHearAboutUs = (value) => {
  const [answer, ...rest] = String(value || "").split(" - ");
  if (!HEAR_ABOUT_US_OPTIONS.includes(answer)) return null;
  return {
    hear_about_us: answer,
    local_shop_name:
      answer === "Local Shop Referral" ? rest.join(" - ").trim() : "",
  };
};

const checkInDefaults = () => {
  const prefs = loadStaffPrefs();
  // Started from a studio appointment on the v2 Today page: the booking's
  // contact details, and it is an appointment at the studio. Garments are
  // never pre-filled (utils/appointmentPrefill.js).
  const appointment = loadPrefill();
  return {
    dropofflocation: appointment ? "STUDIO" : prefs.dropofflocation || "STUDIO",
    intake_person: INTAKE_PEOPLE.includes(prefs.intake_person)
      ? prefs.intake_person
      : "",
    // Opened from the Today page's appointment button = appointment; opened
    // any other way it is someone who walked in. Never carried over from the
    // last order, or one appointment turns every later walk-in into one.
    intake_visit_type: appointment ? "appointment" : "walk-in",
    ...(appointment
      ? {
          firstName: appointment.firstName,
          lastName: appointment.lastName,
          email: appointment.email,
          number: appointment.number,
        }
      : {}),
    // Never remembered between customers: staff must ask every time, or the
    // first option gets saved as the answer. The one exception is the
    // customer's own answer on our booking page, carried by Start order.
    hear_about_us: "",
    ...(bookedHearAboutUs(appointment?.hearAboutUs) || {}),
  };
};

const Checkout = () => {
  const autoCompleteRef = useRef(null);
  const { cartItems, updateCartItem } = useCartStore();
  // Everything typed on this screen before staff stepped out to add work to
  // the order, if they did (utils/checkoutAnswers.js). Read once, and only
  // for this order's own cart.
  const [held] = useState(() => loadCheckoutAnswers(cartItems?.[0]?.id));
  const [deliveryType, setDeliveryType] = useState(
    held?.deliveryType ?? "boutique",
  );
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    defaultValues: { ...checkInDefaults(), ...(held?.form || {}) },
  });
  const userData = JSON.parse(sessionStorage.getItem("userData"));
  const [appointmentPrefill] = useState(loadPrefill);
  // Who the counter's lookup filled in, so the box can say so and be undone.
  // Deliberately not persisted anywhere: one customer's details must not
  // survive into the next order (utils/kioskSession.js wipes the rest).
  const [lookedUp, setLookedUp] = useState(null);
  // An appointment fills the form from the Calendly booking, which may use an
  // address the customer has never ordered under -- so the order would attach
  // to a new customer record beside their real one. This opens the lookup on
  // an appointment order so staff can point it at the right person instead.
  const [lookupAnyway, setLookupAnyway] = useState(false);

  const [total, setTotal] = useState("00.00");
  const [pickupDeliveryFee, setPickupDeliveryFee] = useState(10.0);
  // Starts unticked (Calley, 8 Oct 2026): staff ask, and tick it only if the
  // customer says yes. Before this it started ticked. Keep in step with
  // CHECKOUT_BOX_STARTS_TICKED in fxrybackend/helper/consents.js.
  const [subscribed, setSubscribed] = useState(held?.subscribed ?? false);
  const [smsConsent, setSmsConsent] = useState(held?.smsConsent ?? false);
  const [yesNo, setyesNo] = useState(false);
  const [payment, setPayment] = useState(false);
  const [selected, setSelected] = useState(1);
  const [userInfo, setuserInfo] = useState({
    personalInfo: {},
    address: {},
    deliveryInfo: { collectionDate: null },
    paymentInfo: {},
  });

  const [postDataToApi, setPostDataToApi] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [dpmCheckerLink, setdpmCheckerLink] = useState("");
  const [isPaymentIntentStart, setIsPaymentIntentStart] = useState(false);
  const [paymentMode, setPaymentMode] = useState("terminal"); // "card" | "terminal"
  const [wantsMailBack, setWantsMailBack] = useState(
    held?.wantsMailBack ?? false,
  );
  const [wantsRush, setWantsRush] = useState(held?.wantsRush ?? false);
  const [rushFeeInput, setRushFeeInput] = useState(held?.rushFeeInput ?? "");
  // The ship-back fee is the studio's own Setting (ship_back_fee, $25 by
  // default; never the online shipping fee). Not staff-entered like the rush
  // fee. Until Settings answer it is unknown, and a mail-back order waits
  // rather than guess (utils/serverPricing.js).
  const [shipBackFee, setShipBackFee] = useState(null);
  const [shipBackFeeStatus, setShipBackFeeStatus] = useState("loading");
  const [shipBackFeeRetry, setShipBackFeeRetry] = useState(0);
  // Staff overrides and their reasons. A waived fee is sent as $0 with the
  // reason; the server saves who waived it.
  const [waiveShipBack, setWaiveShipBack] = useState(false);
  const [waivePickup, setWaivePickup] = useState(false);
  const [feeWaiverReason, setFeeWaiverReason] = useState("");
  const [customReasons, setCustomReasons] = useState({});
  // "On us": lines staff give away, by customLineKey -> { on, reason }.
  const [onUs, setOnUs] = useState({});
  // The counter screen faces the customer while they stand there, so the
  // give-away control is not printed under every service any more (Calley,
  // 24 Oct 2026: "we do show this screen to clients at the end - so the 'On
  // us' looks bizarre"). It opens from one word under the total. A line that
  // IS on us keeps its pill and its reason open or closed, because a line
  // nobody paid for is never hidden.
  const [giveOpen, setGiveOpen] = useState(false);
  const cartServices = useMemo(
    () =>
      withOnUs(
        cartItems?.[0]?.services || [],
        onUs,
        cartItems?.[0]?.itemsData || [],
      ),
    [cartItems, onUs],
  );
  const anyOnUs = cartServices.some((service) => service?.on_us === true);
  // The garments in this order, for "+ Add a service".
  const pieces = cartItems?.[0]?.itemsData || [];
  const cartId = cartItems?.[0]?.id;
  // Hold what staff have typed, so stepping out to add work to the order and
  // coming back lands on a screen that still has it. Tied to this cart, and
  // cleared with the rest of sessionStorage between customers.
  //
  // The money decisions are deliberately not held: a promo code, a gift card,
  // a line given away or a waived fee is a judgement made against a bill, and
  // the bill is exactly what just changed. They are made again on the screen
  // that shows what they do. Nothing is held once the payment has started,
  // because nothing on the screen can move then either.
  useEffect(() => {
    if (!cartId || isPaymentIntentStart) return undefined;
    const keep = (form) =>
      saveCheckoutAnswers(cartId, {
        form,
        deliveryType,
        wantsMailBack,
        wantsRush,
        rushFeeInput,
        subscribed,
        smsConsent,
      });
    keep(getValues());
    const typing = watch((values) => keep(values));
    return () => typing.unsubscribe();
  }, [
    cartId,
    isPaymentIntentStart,
    deliveryType,
    wantsMailBack,
    wantsRush,
    rushFeeInput,
    subscribed,
    smsConsent,
    watch,
    getValues,
  ]);
  const setOnUsLine = (service, index, change) =>
    setOnUs((prev) => {
      const key = customLineKey(service, index);
      return { ...prev, [key]: { ...(prev[key] || {}), ...change } };
    });
  const mailOutFee =
    wantsMailBack && !waiveShipBack ? Number(shipBackFee) || 0 : 0;
  const rushFeeAmount = wantsRush ? parseFloat(rushFeeInput) || 0 : 0;
  // SERVER_PRICING on the backend: "on" means the server's quote is the
  // price, shown here and charged. Anything else: this page adds it up, as
  // it always has.
  const [pricingMode, setPricingMode] = useState("off");
  const serverOn = isServerPricingOn(pricingMode);
  const [serverQuote, setServerQuote] = useState(null);
  const [priceNotice, setPriceNotice] = useState(null);
  const pickupFeeCharged =
    deliveryType !== "boutique" && !waivePickup ? pickupDeliveryFee : 0;
  const reasonsMissing = missingReasons({
    services: cartItems?.[0]?.services || [],
    customReasons,
    onUs,
    waiveShipBack: wantsMailBack && waiveShipBack,
    waivePickup: deliveryType !== "boutique" && waivePickup,
    feeWaiverReason,
  });
  const shipBackFeeMissing = wantsMailBack && shipBackFee == null;
  // Which garments are rushed. Customers usually rush one or two pieces, not
  // the whole order (Calley, 2026-09-24). Every garment starts ticked, so
  // rushing the whole order costs no extra clicks. Saved as is_rush on each
  // garment; the backend keeps a partly rushed order on its normal due date
  // (fxrybackend/helper/rushPieces.js).
  const rushGarments = cartItems?.[0]?.itemsData || [];
  const [notRushedIds, setNotRushedIds] = useState(() => new Set());
  const rushedCount = rushGarments.filter(
    (itm) => !notRushedIds.has(itm.id),
  ).length;
  const toggleRushGarment = (id) =>
    setNotRushedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const handleSubscriptionClick = () => {
    setSubscribed(!subscribed);
  };
  const handleYesNoClick = () => {
    setyesNo(!yesNo);
  };
  const handlePaymentClick = () => {
    setPayment(!payment);
  };
  const personalDetailRef = useRef(null);
  const addressDetailRef = useRef(null);
  const deliveryDetailRef = useRef(null);
  const paymentDetailRef = useRef(null);

  const scrollToPersonalDetail = () => {
    personalDetailRef.current.scrollIntoView({ behavior: "smooth" });
    setSelected(1);
  };
  const scrollToAddressDetail = () => {
    addressDetailRef.current.scrollIntoView({ behavior: "smooth" });
    setSelected(2);
  };
  const scrollToDeliveryDetail = () => {
    deliveryDetailRef.current.scrollIntoView({ behavior: "smooth" });
    setSelected(3);
  };
  const scrollToPaymenyDetail = () => {
    paymentDetailRef.current.scrollIntoView({ behavior: "smooth" });
    setSelected(4);
  };

  const onChange = (date) => {
    setuserInfo({
      ...userInfo,
      deliveryInfo: { ...userInfo["deliveryInfo"], collectionDate: date },
    });
  };

  const navigate = useNavigate();
  const [Numbererror, setNumberError] = useState("");

  const validatePhoneNumber = (phoneNumber) => {
    // Strip country code if present
    if (phoneNumber.startsWith("+1")) {
      phoneNumber = phoneNumber.substring(2).trim();
    }

    // Regex pattern for US phone numbers
    const phoneNumberPattern = /^\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}$/;
    return phoneNumberPattern.test(phoneNumber);
  };

  const phoneNumber = watch("number");
  // "STUDIO" (the field's default) means this order was taken in the studio itself, not at
  // an off-site pop-up event (staff overwrite this field with the event name for those).
  // Only in that studio case do we ask whether it was a scheduled weekday appointment or a
  // Saturday walk-in, since both used to be indistinguishable "Popup" orders.
  const dropoffLocationValue = watch("dropofflocation");
  const isStudioIntake =
    (dropoffLocationValue ?? "STUDIO").trim().toUpperCase() === "STUDIO";

  const formatPhoneNumber = (value) => {
    let phoneNumber = value.replace(/[^\d]/g, ""); // Remove all non-numeric characters
    // A pasted or autofilled number often carries the US country code ("+1 206…").
    if (phoneNumber.length === 11 && phoneNumber.startsWith("1")) {
      phoneNumber = phoneNumber.slice(1);
    }
    const regex = /^\(?(\d{3})\)?[-. ]?(\d{3})[-. ]?(\d{4})$/;
    let formattedNumber;

    if (regex.test(phoneNumber)) {
      formattedNumber = phoneNumber.replace(regex, "($1) $2-$3");
      return { formattedNumber, isValid: true };
    } else {
      return { formattedNumber: phoneNumber, isValid: false };
    }
  };

  const [payLoading, setPayLoading] = useState(false);

  useEffect(() => {
    const { formattedNumber, isValid } = formatPhoneNumber(phoneNumber || "");
    setValue("number", formattedNumber, {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [phoneNumber, setValue]);

  // Fetch pickup delivery fee
  useEffect(() => {
    const fetchPickupDeliveryFee = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}fees?t=${Date.now()}`,
          { cache: "no-store" },
        );
        if (response.ok) {
          const data = await response.json();
          setPickupDeliveryFee(data.pickup_delivery_fee);
        }
      } catch (error) {
        console.error("Error fetching pickup delivery fee:", error);
        // Keep default value of 10.00
      }
    };

    fetchPickupDeliveryFee();
  }, []);

  // The studio ship-back fee, from Settings.
  useEffect(() => {
    let cancelled = false;
    setShipBackFeeStatus("loading");
    axios
      .get(`${pathURL}admin/settings`)
      .then((resp) => {
        if (cancelled) return;
        const fee = shipBackFeeFrom(resp?.data);
        setShipBackFee(fee);
        setShipBackFeeStatus(fee == null ? "error" : "ready");
      })
      .catch(() => {
        if (!cancelled) setShipBackFeeStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [shipBackFeeRetry]);

  // Whether the server prices checkouts itself. A failed answer leaves this
  // "off"; if the server is in fact enforcing, the first Pay comes back with
  // "Prices updated" and switches it on.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${pathURL}order/quote`)
      .then((resp) => {
        if (!cancelled && resp?.data?.mode) setPricingMode(resp.data.mode);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const inputChangehandler = (value) => {
    const { type, e } = value;
    // console.log(e.target.name)
    if (e.target.name === "number") {
      // Allow only numbers, dashes, spaces, and parentheses
      if (/^[0-9-\s()]*$/.test(e.target.value)) {
        // Strip country code if present
        let phoneNumber = e.target.value;

        setuserInfo({
          ...userInfo,
          [type]: { ...userInfo[type], [e.target.name]: phoneNumber },
        });

        // Validate phone number
        if (phoneNumber === "" || validatePhoneNumber(phoneNumber)) {
          setNumberError("");
        } else {
          setNumberError(
            "Invalid US phone number. Please enter a valid phone number.",
          );
        }
      }
    } else {
      setuserInfo({
        ...userInfo,
        [type]: { ...userInfo[type], [e.target.name]: e.target.value },
      });
    }
  };
  const [deliveryTypeError, setDeliveryTypeError] = useState(null);
  const [collectionDateError, setCollectionDateError] = useState(null);

  const [subtotalAmount, setsubtotalAmount] = useState(0);
  const [subtotalAmount1, setsubtotalAmount1] = useState(0);
  const [withoutTaxTotal, setwithoutTaxTotal] = useState(0);
  const [subtotalTaxAmount, setsubtotalTaxAmount] = useState(0);
  const [stripeTaxCalculationId, setStripeTaxCalculationId] = useState(null);
  // "loading" | "ready" | "error". Payment can't start until tax is "ready",
  // otherwise a slow or failed tax call charged the customer with no tax.
  const [taxStatus, setTaxStatus] = useState("loading");
  const [taxRetry, setTaxRetry] = useState(0);
  const taxRequestRef = useRef(0);

  // Cart guard + subtotal (services only, no tax)
  useEffect(() => {
    if (!cartItems || cartItems?.length === 0) {
      sessionStorage.removeItem("product-store");
      navigate("/");
      return;
    }
    if (
      cartItems[0]?.itemsData?.length === 0 &&
      cartItems[0]?.services?.length === 0
    ) {
      sessionStorage.removeItem("product-store");
      navigate("/");
      return;
    }
    if (!cartItems[0]?.itemsData || !cartItems[0]?.services) {
      return;
    }

    const subtotal =
      cartServices.reduce((acc, item) => {
        const pricingPlan = cartItems[0]?.itemsData?.find(
          (itm) => item?.item_details?.id === itm?.id,
        );
        if (pricingPlan) {
          const updatedPrice =
            pricingPlan?.pricing_plan === "Standard"
              ? item.standard_price
              : item.premium_price;
          return acc + parseFloat(updatedPrice);
        }
        return acc;
      }, 0) || 0;

    // With the server's price on, the quote sets the subtotal.
    if (!serverOn) setsubtotalAmount1(subtotal);
  }, [cartItems, cartServices, navigate, serverOn]);

  // Stripe Tax via backend (same as fxryshop); popup orders use studio address.
  // Not used with the server's price on: its quote holds the tax.
  useEffect(() => {
    if (serverOn) return;
    const requestId = ++taxRequestRef.current;
    const isLatest = () => requestId === taxRequestRef.current;

    const calculateTax = async () => {
      if (
        !cartItems ||
        cartItems.length === 0 ||
        !cartItems[0]?.services ||
        !cartItems[0]?.itemsData
      ) {
        setsubtotalTaxAmount(0);
        return;
      }

      setTaxStatus("loading");
      try {
        const lineItems = cartServices.map((service, index) => {
          const pricingPlan = cartItems[0].itemsData.find(
            (item) => service?.item_details?.id === item?.id,
          );
          const price =
            pricingPlan?.pricing_plan === "Standard"
              ? service.standard_price
              : service.premium_price;
          const ref =
            service._id != null && service._id !== ""
              ? `svc-${service._id}`
              : service.name ||
                service.selectedOption ||
                `line-${index}-${service?.item_details?.name || "item"}`;
          return {
            amount: Math.round(parseFloat(price) * 100),
            reference: String(ref).slice(0, 120),
          };
        });

        const pickupFeeForTax = pickupFeeCharged;
        // Everything on us and no fees: nothing to tax, nothing to ask.
        if (
          lineItems.every((li) => !(li.amount > 0)) &&
          !(rushFeeAmount > 0) &&
          !(mailOutFee > 0) &&
          !(pickupFeeForTax > 0)
        ) {
          if (!isLatest()) return;
          setsubtotalTaxAmount(0);
          setStripeTaxCalculationId(null);
          setTaxStatus("ready");
          return;
        }
        // Rush fee is taxed the same way a service is — as its own line item —
        // rather than as a delivery fee, since it's a service upcharge.
        const taxLineItems =
          rushFeeAmount > 0
            ? [
                ...lineItems,
                {
                  amount: Math.round(rushFeeAmount * 100),
                  reference: "rush-fee",
                },
              ]
            : lineItems;
        const response = await axios.post(`${pathURL}order/calculate-tax`, {
          customerAddress: STUDIO_TAX_ADDRESS,
          lineItems: taxLineItems.filter((li) => li.amount > 0),
          deliveryFees: {
            inbound: 0,
            return: mailOutFee,
            pickup: pickupFeeForTax,
          },
        });

        // A newer calculation (e.g. the rush fee changed again) supersedes this one.
        if (!isLatest()) return;
        if (response.data?.tax_amount != null) {
          const calculatedTax = parseFloat(response.data.tax_amount);
          setsubtotalTaxAmount(calculatedTax);
          if (response.data.calculation_id && calculatedTax > 0) {
            setStripeTaxCalculationId(response.data.calculation_id);
          } else {
            setStripeTaxCalculationId(null);
          }
          setTaxStatus("ready");
        } else {
          console.warn("[POPUP CHECKOUT] API did not return tax_amount");
          setsubtotalTaxAmount(0);
          setStripeTaxCalculationId(null);
          setTaxStatus("error");
        }
      } catch (error) {
        if (!isLatest()) return;
        console.error("[POPUP CHECKOUT] Error calculating tax:", error);
        setsubtotalTaxAmount(0);
        setStripeTaxCalculationId(null);
        setTaxStatus("error");
      }
    };

    // Typing a rush fee changes the amount on every keystroke; wait for a
    // pause instead of asking for tax on each one.
    setTaxStatus("loading");
    const timer = setTimeout(calculateTax, 400);
    return () => clearTimeout(timer);
  }, [
    cartItems,
    cartServices,
    subtotalAmount1,
    deliveryType,
    pickupDeliveryFee,
    pickupFeeCharged,
    rushFeeAmount,
    mailOutFee,
    taxRetry,
    serverOn,
  ]);

  const [date, setDate] = useState(new Date());

  const [handleDiscErr, setHandleDiscErr] = useState(null);
  const [showdiscount, setShowDiscount] = useState(false);
  const [discount, setDiscount] = useState(null);
  const [discountMeta, setDiscountMeta] = useState(null);
  const [code, setcode] = useState("");

  // Grand total: subtotal + tax + pickup/rush/mail-out fees − discount (percentage includes all fees)
  useEffect(() => {
    if (serverOn || (subtotalAmount1 <= 0 && !anyOnUs)) {
      return;
    }
    const pickupFee = pickupFeeCharged;
    const allFees = pickupFee + rushFeeAmount + mailOutFee;
    const totalWithTaxAndFees =
      subtotalAmount1 +
      subtotalTaxAmount +
      pickupFee +
      rushFeeAmount +
      mailOutFee;
    const totalDiscount = discountMeta
      ? computePopupPromoDiscountAmount(
          discountMeta,
          subtotalAmount1,
          allFees,
          subtotalTaxAmount,
        )
      : 0;

    if (discountMeta) {
      setDiscount(totalDiscount);
      const prevDiscount = cartItems?.[0]?.totalDiscount || 0;
      if (
        Math.abs(prevDiscount - totalDiscount) > 0.001 &&
        cartItems?.[0]?.id
      ) {
        updateCartItem(cartItems[0].id, { totalDiscount });
      }
    }

    setsubtotalAmount(Math.max(0, totalWithTaxAndFees - totalDiscount));
    setwithoutTaxTotal(totalWithTaxAndFees);
  }, [
    subtotalAmount1,
    subtotalTaxAmount,
    pickupDeliveryFee,
    deliveryType,
    discountMeta,
    cartItems,
    updateCartItem,
    rushFeeAmount,
    mailOutFee,
    pickupFeeCharged,
    serverOn,
    anyOnUs,
  ]);

  // ── The server's price (SERVER_PRICING=on) ──────────────────────────────
  // Everything the summary shows comes from POST order/quote, so the
  // discount comes off before tax exactly as the server charges it.
  const watchedEmail = watch("email");
  const watchedIntakePerson = watch("intake_person");
  const overrideReasons = reasonFields({
    waiveShipBack: wantsMailBack && waiveShipBack,
    waivePickup: deliveryType !== "boutique" && waivePickup,
    feeWaiverReason,
  });
  const servicesWithReasons = withCustomReasons(cartServices, customReasons);
  const quoteRequestRef = useRef(0);
  const applyQuote = (quote) => {
    const d = displayFromQuote(quote);
    if (!d) return;
    setServerQuote(quote);
    setsubtotalAmount1(d.servicesSubtotal);
    setsubtotalTaxAmount(d.tax);
    setStripeTaxCalculationId(null);
    setDiscount(d.discountCode && d.discountOk ? d.discount : null);
    setsubtotalAmount(d.total);
    setwithoutTaxTotal(d.total + d.discount);
    setTaxStatus("ready");
  };
  const quoteKey = JSON.stringify(
    quotePayload({
      cart: cartItems?.[0],
      services: servicesWithReasons,
      discountCode: discountMeta?.code || "",
      deliveryType,
      wantsMailBack,
      deliveryFees: popupDeliveryFees({
        wantsMailBack,
        shipBackFee,
        waiveShipBack,
        deliveryType,
        pickupFee: pickupDeliveryFee,
        waivePickup,
      }),
      rushFee: rushFeeAmount,
      reasons: overrideReasons,
      email: watchedEmail,
      intakePerson: watchedIntakePerson,
    }),
  );
  useEffect(() => {
    if (!serverOn) return;
    if (!cartItems?.[0]?.services?.length) return;
    const requestId = ++quoteRequestRef.current;
    setTaxStatus("loading");
    const timer = setTimeout(async () => {
      try {
        const resp = await axios.post(
          `${pathURL}order/quote`,
          { order_payload: JSON.parse(quoteKey), source: "fxrypopup" },
          { headers: authHeader() },
        );
        if (requestId !== quoteRequestRef.current) return;
        if (resp?.data?.mode) setPricingMode(resp.data.mode);
        if (resp?.data?.quote) applyQuote(resp.data.quote);
        else setTaxStatus("error");
      } catch (error) {
        if (requestId !== quoteRequestRef.current) return;
        console.error("[POPUP CHECKOUT] Could not get the price:", error);
        setTaxStatus("error");
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverOn, quoteKey, taxRetry]);
  const quoteView = serverOn ? displayFromQuote(serverQuote) : null;

  /** The server refused a price. Nothing was charged: back to the form,
   *  showing the server's figures, and staff press Pay again. */
  const handlePricingRefusal = (refusal) => {
    setIsPaymentIntentStart(false);
    setPostDataToApi(null);
    setClientSecret("");
    sessionStorage.removeItem("paymentIntentId");
    if (refusal.code === "prices_changed") {
      setPricingMode("on");
      if (refusal.quote) applyQuote(refusal.quote);
      const notice = pricesChangedNotice(refusal.quote);
      setPriceNotice(notice);
      toast.error(notice);
      return;
    }
    setPriceNotice(refusal.message);
    toast.error(refusal.message);
  };

  const handleDiscountApply = async () => {
    setHandleDiscErr(null);
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}discounts/getone/${code}`,
    );
    if (response.status == 200 && response.data && discountMeta == null) {
      const appliedCode = code.toUpperCase();
      setShowDiscount(true);
      if (response?.data?.type == "fixed") {
        const amount = parseFloat(response?.data?.amount);
        setDiscountMeta({
          type: "fixed",
          code: appliedCode,
          fixedAmount: amount,
        });
        toast.success(
          <ToastText
            title={`${appliedCode} applied`}
            detail={`$${amount.toFixed(2)} off your order.`}
          />,
        );
      }
      if (response?.data?.type == "percentage") {
        const percentage = parseFloat(response.data.amount);
        setDiscountMeta({
          type: "percentage",
          code: appliedCode,
          percent: percentage,
        });
        toast.success(
          <ToastText
            title={`${appliedCode} applied`}
            detail={
              percentage >= 100
                ? "100% off your order."
                : `${percentage}% off services and fees.`
            }
          />,
        );
      }
      return;
    }

    if (!response.data) {
      toast.error(
        <ToastText
          title="We don't recognize that code"
          detail="Check the spelling and try again."
        />,
      );
      setcode("");
    }

    if (response?.data?.length == 0) {
      setHandleDiscErr("This code can't be used on this order.");
    }
  };

  // Take one line off the order from the summary. Removing a garment's last
  // service removes the garment too; removing the only one empties the basket
  // and the cart guard above sends checkout back to the start. Hidden once the
  // payment intent exists, like "+ Add another item": the amount is fixed then.
  const removeService = (index) => {
    const cart = cartItems?.[0];
    if (!cart?.id) return;
    if (
      (cart.services || []).length === 1 &&
      !window.confirm(
        "That's the only thing in the basket. Remove it and start over?",
      )
    ) {
      return;
    }
    updateCartItem(cart.id, withoutService(cart, index));
  };

  const removeDiscount = () => {
    setShowDiscount(false);
    setDiscount(null);
    setDiscountMeta(null);
    setcode("");
    setHandleDiscErr(null);
    updateCartItem(cartItems[0].id, {
      totalDiscount: 0,
    });
  };

  const handleCodeSet = (e) => {
    if (e.target.value != "") {
      setcode(e.target.value.toUpperCase());
    } else {
      removeDiscount();
    }
  };

  // ── Gift certificate ─────────────────────────────────────────────────────
  // A way of paying, not a discount (utils/giftCertificate.js). It comes off
  // the total after tax, fees and any discount code, and what it pays is
  // worked out from the current total on every render, so a total that drops
  // (a line removed, a discount added) takes less off the certificate.
  const [giftEnabled, setGiftEnabled] = useState(false);
  const [giftInput, setGiftInput] = useState("");
  const [gift, setGift] = useState(null); // {code, display_code, balance_cents}
  const [giftErr, setGiftErr] = useState(null);
  const [giftChecking, setGiftChecking] = useState(false);

  // The Owner switches certificates on from the staff app; until then the
  // box doesn't show at all.
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${pathURL}gift/options`)
      .then((resp) => {
        if (!cancelled) setGiftEnabled(resp?.data?.enabled === true);
      })
      .catch(() => {
        if (!cancelled) setGiftEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Selling a gift card on this bill ─────────────────────────────────────
  // Someone adding a gift card to their repair. The card is reserved on the
  // server as soon as staff add it (an unpaid counter sale), its amount goes
  // on what the reader charges, and the same payment makes it live. It never
  // touches the tax, the discount or the order's total: a gift card is money
  // we owe until it is spent.
  const [giftSale, setGiftSale] = useState(null); // {id, face_cents, display_code}
  const [giftSaleAmount, setGiftSaleAmount] = useState("");
  const [giftSaleCode, setGiftSaleCode] = useState("");
  const [giftSaleErr, setGiftSaleErr] = useState(null);
  // Which of the three sometimes-tools is open under the total: "promo",
  // "gift" (paying with a card), "sell" (selling one), or nothing. The
  // counter uses none of them on most orders, so none of them are open
  // until someone asks for one (Calley, 24 Oct 2026).
  const [openTool, setOpenTool] = useState(null);
  // Which piece a new service goes on. Checkout could only start a whole new
  // garment ("+ Add another item"), so another fix on a piece already in the
  // basket meant going back (Calley, 24 Oct 2026: "we lost the ability to add
  // things to an item in checkout"). With one piece in the basket this goes
  // straight to its service list; with several it asks which one first.
  const [pickPiece, setPickPiece] = useState(false);
  const [giftSaleBusy, setGiftSaleBusy] = useState(false);

  // Same session keys the basket screen sets (ItemDetail.jsx addService), so
  // the service screen opens on this piece and the row comes back onto it.
  const addServiceToPiece = (piece) => {
    sessionStorage.setItem("addnewservice", true);
    sessionStorage.setItem("SelectedPlan", piece?.pricing_plan);
    sessionStorage.setItem("selectedItem", piece?.name);
    sessionStorage.setItem("selectedItems", JSON.stringify(piece));
    navigate("/alteration", {
      state: { addNewService: true, selectedItem: piece, addNewItem: false },
    });
  };

  const addGiftSale = async () => {
    const amountCents = giftSaleAmountCents(giftSaleAmount);
    const refusal = giftSaleRefusal({ amountCents, code: giftSaleCode });
    if (refusal) {
      setGiftSaleErr(refusal);
      return;
    }
    setGiftSaleErr(null);
    setGiftSaleBusy(true);
    try {
      const resp = await axios.post(
        `${pathURL}gift/sell`,
        {
          amount_cents: amountCents,
          card_code: giftSaleCode.trim(),
          buyer: { name: "", email: "" },
          send_to: "buyer",
          message: "",
          // No Stripe page: this bill's payment pays for it.
          pay: "reader",
        },
        { headers: authHeader() },
      );
      setGiftSale({
        id: resp?.data?.id,
        face_cents: amountCents,
        display_code: resp?.data?.display_code || "",
      });
      setGiftSaleAmount("");
      setGiftSaleCode("");
    } catch (error) {
      setGiftSaleErr(
        error?.response?.data?.message ||
          "Couldn't add that gift card. Check the code and try again.",
      );
    } finally {
      setGiftSaleBusy(false);
    }
  };

  const dueCents =
    serverOn && quoteView
      ? Math.max(0, quoteView.totalCents)
      : Math.round(Math.max(0, subtotalAmount) * 100);
  const {
    appliedCents: giftAppliedCents,
    payCents,
    coversAll: giftCoversAll,
  } = giftSplit(dueCents, gift ? gift.balance_cents : 0);

  const clearGift = (message = null) => {
    setGift(null);
    setGiftInput("");
    setGiftErr(message);
  };

  const handleGiftApply = async () => {
    const typed = giftInput.trim();
    if (!typed) return;
    setGiftErr(null);
    setGiftChecking(true);
    try {
      const resp = await axios.get(
        `${pathURL}gift/check/${encodeURIComponent(typed)}`,
      );
      const data = resp?.data;
      if (data?.ok && data.code) {
        setGift({
          code: data.code,
          display_code: data.display_code || data.code,
          balance_cents: Math.max(
            0,
            Math.round(Number(data.balance_cents) || 0),
          ),
        });
        setGiftInput("");
      } else {
        setGiftErr(data?.message || "We can't find that gift card code.");
      }
    } catch (error) {
      setGiftErr(
        error?.response?.data?.message ||
          "Couldn't check that code. Check the connection and try again.",
      );
    } finally {
      setGiftChecking(false);
    }
  };

  /** The server refused to start a payment. If the certificate no longer
   *  covers what this order is counting on (spent elsewhere meanwhile), drop
   *  it so staff can re-enter it and use the new balance. Any other refusal
   *  is shown as is and the certificate stays. */
  const handleGiftRefusal = async (message) => {
    let stillCovers = false;
    try {
      const resp = await axios.get(
        `${pathURL}gift/check/${encodeURIComponent(gift?.code || "")}`,
      );
      stillCovers =
        resp?.data?.ok === true &&
        Number(resp.data.balance_cents) >= giftAppliedCents;
    } catch {
      stillCovers = false;
    }
    if (!stillCovers) clearGift(message);
    toast.error(message);
  };

  const handlePlaceSelect = () => {
    const addressObject = autoCompleteRef.current.getPlace();
    if (addressObject) {
      const addressComponents = addressObject.address_components;
      let streetNumber = "";
      let route = "";

      addressComponents.forEach((component) => {
        const types = component.types;
        if (types.includes("subpremise")) {
          setValue("line2", component.long_name);
        }
        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (types.includes("route")) {
          route = component.long_name;
        }
        if (types.includes("locality")) {
          setValue("city", component.long_name);
        }
        if (types.includes("administrative_area_level_1")) {
          setValue("state", component.long_name);
        }
        if (types.includes("postal_code")) {
          setValue("code", component.long_name);
        }
      });

      // Merge streetNumber and route
      const addressText = `${streetNumber} ${route}`.trim();
      setValue("addressText", addressText);
      setValue("line1", addressText);
    }
  };

  // A second tap while the first is still starting payment used to create a
  // second payment. The ref blocks it immediately; the state disables the button.
  const submittingRef = useRef(false);
  const [submitting, setSubmitting] = useState(false);

  // With the server's price on, only its total says an order is free (a comp
  // already used by this customer is not).
  const isComplimentary = serverOn
    ? Boolean(quoteView) && quoteView.totalCents <= 0
    : (discountMeta?.type === "percentage" &&
        parseFloat(discountMeta.percent) >= 100) ||
      (subtotalAmount <= 0 && (discount != null || anyOnUs));

  const handleApply = async (data) => {
    if (submittingRef.current) return;
    if (!isComplimentary && taxStatus !== "ready") {
      toast.error("Still working out tax. Try again in a moment.");
      return;
    }
    if (reasonsMissing.length) {
      toast.error(reasonsMissing[0]);
      return;
    }
    if (shipBackFeeMissing) {
      toast.error("Still loading the ship-back fee. Try again in a moment.");
      return;
    }
    setPriceNotice(null);
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await placeOrder(data);
    } catch (error) {
      console.error("[POPUP CHECKOUT] Could not start payment:", error);
      toast.error(
        error.response?.data?.error ||
          "Couldn't start the payment. Check the connection and try again.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const placeOrder = async (data) => {
    if (data?.dropofflocation == null) {
      setCollectionDateError("Please enter the drop-off location!");
      return;
    }
    if (wantsRush && rushGarments.length > 1 && rushedCount === 0) {
      toast.error("Tick at least one piece to rush.");
      return;
    }
    if (wantsRush && rushFeeAmount <= 0) {
      toast.error("Enter the rush fee.");
      return;
    }
    if (discount != null) {
      const resp = await axios.put(
        `${process.env.REACT_APP_API_URL}discounts/checkconsumerexists`,
        {
          email: data?.email,
          discount_code: code.toUpperCase(),
        },
      );
      if (resp.data == true) {
        removeDiscount();
        toast.error("This code can't be used on this order.");
        return;
      }
    }
    // Remember the check-in choices for the next customer on this device.
    saveStaffPrefs({
      ...loadStaffPrefs(),
      dropofflocation: data.dropofflocation,
      intake_person: data.intake_person,
    });
    const fullName =
      `${(data?.firstName || "").trim()} ${(data?.lastName || "").trim()}`.trim();
    updateCartItem(cartItems[0].id, {
      user: {
        email: data?.email,
        fullName: fullName,
        number: data.number,
        subscribed,
        smsConsent,
        smsConsentSource: "studio checkout",
        address: {
          addressText: data.addressText,
          line1: data.line1,
          line2: data.line2,
          city: data.city,
          code: data.code,
          state: data?.state,
        },
        deliveryInfo: { ...userInfo.deliveryInfo, leavewithneighbour: yesNo },
        location: data?.dropofflocation,
      },
    });
    const postData = cartItems.map((item) => {
      return {
        ...item,
        itemsData: wantsRush
          ? (item.itemsData || []).map((itm) => ({
              ...itm,
              is_rush: !notRushedIds.has(itm.id),
            }))
          : item.itemsData,
        location: data?.dropofflocation,
        type: "Popup",
        user: {
          personalInfo: {
            email: data?.email,
            fullName: fullName,
            number: data.number,
            subscribed,
            smsConsent,
            smsConsentSource: "studio checkout",
            address: {
              addressText: data.addressText,
              line1: data.line1,
              line2: data.line2,
              city: data.city,
              code: data.code,
              state: data?.state,
            },
          },
          deliveryInfo: userInfo.deliveryInfo,
          location: data?.dropofflocation,
          type: "Popup",
          address: wantsMailBack
            ? {
                addressText: data.addressText,
                line1: data.addressText,
                line2: data.line2,
                city: data.city,
                code: data.code,
                state: data?.state,
              }
            : {
                addressText: data?.dropofflocation,
                line1: data.line1,
                line2: data.line2,
                city: data.city,
                code: data.code,
                state: data?.state,
              },
          paymentInfo: {},
        },
        discountCode: code.toUpperCase(),
        discountValue: discount,
        totalDiscount: discount ?? 0,
        deliveryType: deliveryType,
        return_delivery_method: wantsMailBack ? "ship" : "studio-pickup",
        rush_due_date:
          wantsRush && data.rushDueDate
            ? new Date(`${data.rushDueDate}T12:00:00`).toISOString()
            : null,
        intake_visit_type: isStudioIntake ? data.intake_visit_type : null,
        intake_person: data.intake_person,
        hear_about_us:
          data.hear_about_us === "Local Shop Referral" &&
          data.local_shop_name?.trim()
            ? `Local Shop Referral - ${data.local_shop_name.trim()}`
            : data.hear_about_us,
        services: servicesWithReasons,
        delivery_fees: {
          inbound: 0,
          return: mailOutFee,
          pickup: pickupFeeCharged,
        },
        rush_fee: rushFeeAmount,
        ...overrideReasons,
        stripe_tax_calculation_id: stripeTaxCalculationId,
        // Paid in full by a certificate there is no PaymentIntent to link
        // the tax calculation to; the backend records the tax itself.
        stripe_tax_pi_linked: giftCoversAll
          ? false
          : Boolean(stripeTaxCalculationId),
        ...giftPayloadFields(gift, giftAppliedCents),
        ...giftSaleFields(giftSale),
      };
    });
    // setPayLoading(true);
    // const stripe = await loadStripe(process.env.REACT_APP_STRIPE_API_KEY);
    // const response = await axios.post(`${pathURL}order/create`, postData[0]);
    // if (response.status == 200) {
    //   const sessionId = response.data.sessionId;
    //   const result = stripe.redirectToCheckout({
    //     sessionId: sessionId,
    //   });
    // }
    // setPayLoading(true);
    // const response = await axios.post(`${pathURL}order/create`, postData[0]);
    // What the card or reader takes: the total after discount, less whatever
    // the gift certificate pays, plus a gift card bought on the same bill.
    const payableCents = chargedWithGiftSale(payCents, giftSale);
    const isFullyDiscounted = serverOn
      ? dueCents <= 0
      : dueCents <= 0 ||
        (discountMeta?.type === "percentage" &&
          parseFloat(discountMeta.percent) >= 100);
    const paidByGift = !isFullyDiscounted && giftCoversAll;

    if (paidByGift) {
      // Check the certificate still covers it before the order is written.
      // The server redeems it and refuses the order if it doesn't; this just
      // stops an unpaid order being created in the meantime.
      try {
        const check = await axios.get(
          `${pathURL}gift/check/${encodeURIComponent(gift.code)}`,
        );
        if (
          !check?.data?.ok ||
          Number(check.data.balance_cents) < giftAppliedCents
        ) {
          const message =
            check?.data?.message ||
            `That gift card has ${centsToDollars(
              check?.data?.balance_cents,
            )} left. Enter the code again to use the new balance.`;
          clearGift(message);
          toast.error(message);
          return;
        }
      } catch (error) {
        toast.error(
          error?.response?.data?.message ||
            "Couldn't check the gift card. Check the connection and try again.",
        );
        return;
      }
    }

    if (isFullyDiscounted || paidByGift) {
      try {
        setPayLoading(true);
        console.log(
          paidByGift
            ? "[POPUP CHECKOUT] Paid in full by gift card — bypassing Stripe payment"
            : "[POPUP CHECKOUT] Fully discounted order — bypassing Stripe payment",
        );
        const response = await axios.post(
          `${process.env.REACT_APP_API_URL}order/createforpaymentintent`,
          {
            ...postData[0],
            totalDiscount: discount ?? postData[0].totalDiscount ?? 0,
            discountValue: discount,
          },
          // Who took the order. The server never refuses without it.
          { headers: authHeader() },
        );
        if (response.status === 200) {
          const orderId = response?.data?.data?._id;
          sessionStorage.setItem("orderId", orderId);
          sessionStorage.setItem("thankyoupage", "yes");
          sessionStorage.removeItem("pendingOrderData");
          await axios.post(
            `${process.env.REACT_APP_API_URL}order/callbackpaymentintent/${orderId}`,
            {
              payment: "paid",
              payment_intent: paidByGift
                ? `giftcert_full_${orderId}`
                : `discount_full_${orderId}`,
            },
          );
          navigate("/thankyou");
        } else {
          toast.error("We couldn't place your order. Try again.");
        }
      } catch (error) {
        console.error("[POPUP CHECKOUT] Complimentary order failed:", error);
        const refusal = pricingRefusal(error);
        if (refusal) {
          handlePricingRefusal(refusal);
          return;
        }
        toast.error(
          error.response?.data?.error ||
            error.response?.data?.message ||
            "We couldn't place your order. Try again.",
        );
      } finally {
        setPayLoading(false);
      }
      return;
    }

    sessionStorage.removeItem("paymentIntentId");
    sessionStorage.removeItem("orderId");
    sessionStorage.removeItem("pendingOrderData");

    if (paymentMode === "terminal") {
      // Terminal handles its own PaymentIntent (card-present) and only
      // creates the order after the reader confirms success — see
      // TerminalCheckoutForm.jsx.
      setPostDataToApi(postData);
      setIsPaymentIntentStart(true);
      return;
    }

    if (!process.env.REACT_APP_STRIPE_API_KEY) {
      console.error(
        "[POPUP CHECKOUT] REACT_APP_STRIPE_API_KEY is not configured; payment cannot start.",
      );
      toast.error(
        "Payments aren't working right now. Try again in a few minutes, or email hello@thefxry.com.",
      );
      return;
    }

    // The address and the cart go with the payment request. The server checks
    // the address with the carrier BEFORE the intent exists, so a return
    // address it will not accept is caught while nothing has been charged,
    // rather than after, when the order is refused and the charge stands.
    // The cart is parked server-side so a screen that never reaches the
    // Thank You step cannot lose the order.
    let responsePayment;
    try {
      responsePayment = await axios.post(
        `${process.env.REACT_APP_API_URL}order/createpaymentintent`,
        {
          amount: payableCents,
          name: fullName,
          email: data?.email,
          disableLink: true,
          taxCalculationId: stripeTaxCalculationId || undefined,
          ...giftSaleFields(giftSale),
          source: "fxrypopup",
          address: postData[0]?.user?.address,
          inbound_delivery_method: postData[0]?.inbound_delivery_method,
          return_delivery_method: postData[0]?.return_delivery_method,
          order_payload: postData[0],
        },
        { headers: authHeader() },
      );
    } catch (error) {
      const pricing = pricingRefusal(error);
      if (pricing) {
        handlePricingRefusal(pricing);
        return;
      }
      const refusal = error?.response?.data;
      if (error?.response?.status === 400 && refusal?.field === "address") {
        toast.error(
          `The carrier won't accept that return address: ${refusal.error} Nothing has been charged. Fix the address and try again.`,
        );
        return;
      }
      // The certificate was spent elsewhere since it was checked.
      if (error?.response?.status === 400 && refusal?.message && gift) {
        await handleGiftRefusal(refusal.message);
        return;
      }
      console.error(
        "[POPUP CHECKOUT] could not start payment:",
        error?.message,
      );
      toast.error(
        "Couldn't start the payment. Nothing has been charged. Try again in a moment.",
      );
      return;
    }
    setIsPaymentIntentStart(true);
    setPostDataToApi(postData);
    sessionStorage.setItem(
      "paymentIntentId",
      responsePayment.data.clientsecret,
    );
    setClientSecret(responsePayment.data.clientsecret);
    setdpmCheckerLink(responsePayment.data.dpmcheckerlink);
  };

  useEffect(() => {
    if (autoCompleteRef.current) {
      autoCompleteRef.current.setFields([
        "address_components",
        "formatted_address",
      ]);
    }
  }, []);

  const appearance = {
    theme: "stripe",
  };
  const options = {
    clientSecret,
    appearance,
  };

  const stripePromise = process.env.REACT_APP_STRIPE_API_KEY
    ? loadStripe(process.env.REACT_APP_STRIPE_API_KEY)
    : null;

  return (
    <section className="checkout-section item-detail-section items-section same-bg-section relative">
      {payLoading ? (
        <Loader />
      ) : (
        <div className="container padding-x-all">
          <div className="item-detail-box sec-com-padding flex-box justify-between align-center">
            <div className="checkout-left-box">
              <div className="promo-code-box">
                <div className="checkout-sub-total">
                  {cartItems[0]?.services?.map((carts, index) => {
                    let pricingPlan = cartItems[0]?.itemsData?.find((itm) => {
                      return carts?.item_details?.id === itm?.id;
                    });
                    const given = onUsEntry(onUs, carts, index);
                    const priced = cartServices[index] || carts;
                    return (
                      <div key={index} className="checkout-line-wrap">
                        <div className="flex-box justify-between checkout-line">
                          <h6>{carts?.name}</h6>
                          <div className="checkout-line__right">
                            {given && (
                              <span className="checkout-line__was">
                                $
                                {pricingPlan?.pricing_plan === "Standard"
                                  ? carts.standard_price
                                  : carts.premium_price}
                              </span>
                            )}
                            <h6>
                              $
                              {quoteView &&
                              quoteView.linePrices[index] !== undefined
                                ? quoteView.linePrices[index].toFixed(2)
                                : pricingPlan?.pricing_plan === "Standard"
                                  ? priced.standard_price
                                  : priced.premium_price}
                            </h6>
                            {!isPaymentIntentStart && (
                              <button
                                type="button"
                                className="checkout-line__remove"
                                aria-label={`Remove ${carts?.name}`}
                                title="Remove"
                                onClick={() => removeService(index)}
                              >
                                ×
                              </button>
                            )}
                          </div>
                        </div>
                        {!isPaymentIntentStart && (giveOpen || given) && (
                          <div className="checkout-line__give">
                            <button
                              type="button"
                              className={`checkout-line__on-us${given ? " on" : ""}`}
                              aria-pressed={Boolean(given)}
                              title="Give this one away"
                              onClick={() =>
                                setOnUsLine(carts, index, { on: !given })
                              }
                            >
                              On us
                            </button>
                          </div>
                        )}
                        {given && (
                          <input
                            type="text"
                            className={`checkout-line__why${
                              String(given.reason ?? "").trim() ? "" : " empty"
                            }`}
                            maxLength={200}
                            disabled={isPaymentIntentStart}
                            placeholder="Why is this on us? e.g. promised to Allegra, Oct 18"
                            aria-label={`Why ${carts?.name} is on us`}
                            value={given.reason || ""}
                            onChange={(e) =>
                              setOnUsLine(carts, index, {
                                reason: e.target.value,
                              })
                            }
                          />
                        )}
                      </div>
                    );
                  })}
                  {deliveryType != "boutique" && (
                    <div className="flex-box justify-between">
                      <h6>
                        Pickup & Drop-off fee{waivePickup ? " (waived)" : ""}
                      </h6>
                      <h6>${pickupFeeCharged.toFixed(2)}</h6>
                    </div>
                  )}
                  {(mailOutFee > 0 || (wantsMailBack && waiveShipBack)) && (
                    <div className="flex-box justify-between">
                      <h6>Mail-out fee{waiveShipBack ? " (waived)" : ""}</h6>
                      <h6>${mailOutFee.toFixed(2)}</h6>
                    </div>
                  )}
                  {rushFeeAmount > 0 && (
                    <div className="flex-box justify-between">
                      <h6>Rush fee</h6>
                      <h6>${rushFeeAmount.toFixed(2)}</h6>
                    </div>
                  )}
                  <div className="checkout-summary-row flex-box justify-between">
                    <h6>
                      Subtotal ({cartItems[0]?.services?.length || 0}{" "}
                      {cartItems[0]?.services?.length === 1 ? "item" : "items"})
                    </h6>
                    <h6>${subtotalAmount1.toFixed(2)}</h6>
                  </div>
                  {showdiscount && discount != null && (
                    <div className="checkout-summary-row checkout-discount-row flex-box justify-between">
                      <h6>
                        Discount
                        {discountMeta?.code && (
                          <span className="checkout-discount-row__code">
                            {discountMeta.code}
                          </span>
                        )}
                      </h6>
                      <h6>−${discount.toFixed(2)}</h6>
                    </div>
                  )}
                  <div className="checkout-summary-row flex-box justify-between">
                    <h6>Total before tax</h6>
                    <h6>
                      $
                      {Math.max(0, subtotalAmount - subtotalTaxAmount).toFixed(
                        2,
                      )}
                    </h6>
                  </div>
                  <div className="checkout-summary-row flex-box justify-between">
                    <h6>Total Tax</h6>
                    <h6>${subtotalTaxAmount.toFixed(2)}</h6>
                  </div>
                </div>
                {giftAppliedCents > 0 && (
                  <div className="checkout-sub-total checkout-gift-lines">
                    <div className="checkout-summary-row flex-box justify-between">
                      <h6>Total</h6>
                      <h6>{centsToDollars(dueCents)}</h6>
                    </div>
                    <div className="checkout-summary-row checkout-discount-row flex-box justify-between">
                      <h6>
                        Gift card
                        <span className="checkout-discount-row__code">
                          {String(gift.display_code).slice(0, 4)}-…
                        </span>
                      </h6>
                      <h6>−{centsToDollars(giftAppliedCents)}</h6>
                    </div>
                  </div>
                )}
                {priceNotice && (
                  <p className="promo-code-error" role="alert">
                    {priceNotice}
                  </p>
                )}
                {/* A gift card bought on this bill. Its own line, because it
                    is not part of the repair's total or its tax: the line
                    above is the repair, this is the card, and the figure
                    underneath is what the reader asks for. */}
                {giftSale && (
                  <div className="checkout-sub-total checkout-gift-lines">
                    <div className="checkout-summary-row flex-box justify-between">
                      <h6>
                        Gift card
                        <span className="checkout-discount-row__code">
                          {String(giftSale.display_code || "").slice(0, 4)}-…
                        </span>
                      </h6>
                      <h6>{centsToDollars(giftSale.face_cents)}</h6>
                    </div>
                  </div>
                )}
                <div className="checkout-total flex-box justify-between">
                  <h5>
                    {giftSale
                      ? "To pay"
                      : giftAppliedCents > 0
                        ? "Left to pay"
                        : "Total"}
                  </h5>
                  <h5>
                    {giftSale ? (
                      centsToDollars(chargedWithGiftSale(payCents, giftSale))
                    ) : giftAppliedCents > 0 ? (
                      centsToDollars(payCents)
                    ) : (
                      <>${subtotalAmount.toFixed(2)}</>
                    )}
                  </h5>
                </div>
                {/* Promo code, paying with a gift card, and giving a line
                    away. All three are rare at the counter, and the first two
                    used to sit above the order as open forms (Calley, 24 Oct
                    2026: "we barely use promo cards, gift cards or sell them
                    compared to how often we use the checkout"). They are
                    three words under the total now, and open only when asked
                    for. Anything already applied keeps showing either way,
                    because money off the bill is never hidden. */}
                <div className="checkout-tools">
                  {!isPaymentIntentStart && (
                    <div className="checkout-tools__row">
                      {!showdiscount && (
                        <button
                          type="button"
                          className={`checkout-tools__link${openTool === "promo" ? " on" : ""}`}
                          aria-expanded={openTool === "promo"}
                          onClick={() =>
                            setOpenTool(openTool === "promo" ? null : "promo")
                          }
                        >
                          Promo code
                        </button>
                      )}
                      {giftEnabled && !gift && (
                        <button
                          type="button"
                          className={`checkout-tools__link${openTool === "gift" ? " on" : ""}`}
                          aria-expanded={openTool === "gift"}
                          onClick={() =>
                            setOpenTool(openTool === "gift" ? null : "gift")
                          }
                        >
                          Gift card
                        </button>
                      )}
                      <button
                        type="button"
                        className={`checkout-tools__link${giveOpen ? " on" : ""}`}
                        aria-pressed={giveOpen}
                        onClick={() => setGiveOpen(!giveOpen)}
                      >
                        Give a line away
                      </button>
                    </div>
                  )}
                  {(showdiscount || openTool === "promo") && (
                    <div className="checkout-tools__panel">
                      {!showdiscount ? (
                        <form className="promo-code">
                          <input
                            type="text"
                            placeholder="Promo code"
                            value={code}
                            onChange={handleCodeSet}
                          />
                          <button
                            className="promo-code-btn"
                            type="button"
                            disabled={code === ""}
                            onClick={handleDiscountApply}
                          >
                            Apply
                          </button>
                        </form>
                      ) : (
                        <div className="promo-applied-banner">
                          <div className="promo-applied-banner__text">
                            <span className="promo-applied-banner__code">
                              {discountMeta?.code || code}
                            </span>
                            <span className="promo-applied-banner__detail">
                              {discountMeta?.type === "percentage"
                                ? parseFloat(discountMeta.percent) >= 100
                                  ? "100% off services, fees and tax"
                                  : `${discountMeta.percent}% off services and fees`
                                : `$${Number(discountMeta?.fixedAmount || 0).toFixed(2)} off`}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="promo-applied-banner__remove"
                            onClick={removeDiscount}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                  {handleDiscErr != null && (
                    <p className="promo-code-error">{handleDiscErr}</p>
                  )}
                  {quoteView?.discountNote && (
                    <p className="promo-code-error">{quoteView.discountNote}</p>
                  )}
                  {giftEnabled && (gift || openTool === "gift") && (
                    <div className="checkout-tools__panel gift-code-box">
                      {!gift ? (
                        <form
                          className="promo-code gift-code"
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleGiftApply();
                          }}
                        >
                          <input
                            type="text"
                            placeholder="Gift card code"
                            aria-label="Gift card code"
                            autoComplete="off"
                            spellCheck={false}
                            value={giftInput}
                            disabled={isPaymentIntentStart}
                            onChange={(e) => {
                              setGiftInput(e.target.value.toUpperCase());
                              setGiftErr(null);
                            }}
                          />
                          <button
                            className="promo-code-btn"
                            type="submit"
                            disabled={
                              giftInput.trim() === "" ||
                              giftChecking ||
                              isPaymentIntentStart
                            }
                          >
                            {giftChecking ? "Checking" : "Apply"}
                          </button>
                        </form>
                      ) : (
                        <div className="promo-applied-banner gift-applied-banner">
                          <div className="promo-applied-banner__text">
                            <span className="gift-applied-banner__label">
                              Gift card
                            </span>
                            <span className="promo-applied-banner__code">
                              {gift.display_code}
                            </span>
                            <span className="promo-applied-banner__detail">
                              {giftAppliedCents > 0 &&
                              giftAppliedCents >= gift.balance_cents
                                ? `Using all ${centsToDollars(gift.balance_cents)}.`
                                : giftAppliedCents > 0
                                  ? `Balance ${centsToDollars(
                                      gift.balance_cents,
                                    )}. Using ${centsToDollars(
                                      giftAppliedCents,
                                    )}, ${centsToDollars(
                                      gift.balance_cents - giftAppliedCents,
                                    )} stays on it.`
                                  : `Balance ${centsToDollars(
                                      gift.balance_cents,
                                    )}.${dueCents <= 0 ? " Nothing to pay on this order." : ""}`}
                            </span>
                          </div>
                          {!isPaymentIntentStart && (
                            <button
                              type="button"
                              className="promo-applied-banner__remove"
                              onClick={() => clearGift()}
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      )}
                      {giftErr && <p className="promo-code-error">{giftErr}</p>}
                    </div>
                  )}
                </div>
                {/* Choosing "add another item" is only offered from the basket
                    screen before this one, so changing your mind here meant
                    the browser Back button or starting over. Hidden once the
                    payment intent exists — leaving mid-payment would strand
                    a charge. */}
                {!isPaymentIntentStart && (
                  <div className="checkout-add-row">
                    {pieces.length > 0 && (
                      <button
                        type="button"
                        className={`checkout-add-item${pickPiece ? " on" : ""}`}
                        aria-expanded={
                          pieces.length > 1 ? pickPiece : undefined
                        }
                        onClick={() =>
                          pieces.length === 1
                            ? addServiceToPiece(pieces[0])
                            : setPickPiece(!pickPiece)
                        }
                      >
                        + Add a service
                      </button>
                    )}
                    <button
                      type="button"
                      className="checkout-add-item"
                      onClick={() => {
                        sessionStorage.setItem("addnewitem", true);
                        navigate("/types", { state: { addNewItem: true } });
                      }}
                    >
                      + Add another item
                    </button>
                    {giftEnabled && !giftSale && (
                      <button
                        type="button"
                        className={`checkout-add-item${openTool === "sell" ? " on" : ""}`}
                        aria-expanded={openTool === "sell"}
                        onClick={() =>
                          setOpenTool(openTool === "sell" ? null : "sell")
                        }
                      >
                        + Sell a gift card
                      </button>
                    )}
                  </div>
                )}
                {!isPaymentIntentStart && pickPiece && pieces.length > 1 && (
                  <div className="checkout-piece-pick">
                    <p className="checkout-piece-pick__ask">
                      Which piece is it on?
                    </p>
                    <div className="checkout-piece-pick__row">
                      {pieces.map((piece, index) => (
                        <button
                          key={piece?.id || index}
                          type="button"
                          className="checkout-add-item"
                          onClick={() => addServiceToPiece(piece)}
                        >
                          {String(piece?.name || "").trim()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Selling a gift card is not a way of paying for this order, it
                    adds a line to it, which is why it sits with "add another
                    item" and not with the two above. */}
                {giftEnabled && (giftSale || openTool === "sell") && (
                  <div className="checkout-tools__panel gift-code-box gift-sale-box">
                    {!giftSale ? (
                      <form
                        className="promo-code gift-code"
                        onSubmit={(e) => {
                          e.preventDefault();
                          addGiftSale();
                        }}
                      >
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="Gift card $"
                          aria-label="Gift card amount in dollars"
                          autoComplete="off"
                          className="gift-sale-amount"
                          value={giftSaleAmount}
                          disabled={isPaymentIntentStart || giftSaleBusy}
                          onChange={(e) => {
                            setGiftSaleAmount(e.target.value);
                            setGiftSaleErr(null);
                          }}
                        />
                        <input
                          type="text"
                          placeholder="Code on the card"
                          aria-label="Code on the back of the printed card"
                          autoComplete="off"
                          spellCheck={false}
                          value={giftSaleCode}
                          disabled={isPaymentIntentStart || giftSaleBusy}
                          onChange={(e) => {
                            setGiftSaleCode(e.target.value.toUpperCase());
                            setGiftSaleErr(null);
                          }}
                        />
                        <button
                          className="promo-code-btn"
                          type="submit"
                          disabled={
                            giftSaleAmount.trim() === "" ||
                            giftSaleCode.trim() === "" ||
                            giftSaleBusy ||
                            isPaymentIntentStart
                          }
                        >
                          {giftSaleBusy ? "Adding" : "Sell a gift card"}
                        </button>
                      </form>
                    ) : (
                      <div className="promo-applied-banner gift-applied-banner">
                        <div className="promo-applied-banner__text">
                          <span className="gift-applied-banner__label">
                            Gift card sold
                          </span>
                          <span className="promo-applied-banner__code">
                            {giftSale.display_code}
                          </span>
                          <span className="promo-applied-banner__detail">
                            {centsToDollars(giftSale.face_cents)} on this bill.
                            It goes live when the payment goes through, then
                            hand the card over.
                          </span>
                        </div>
                        {!isPaymentIntentStart && (
                          <button
                            type="button"
                            className="promo-applied-banner__remove"
                            onClick={() => {
                              setGiftSale(null);
                              setGiftSaleErr(null);
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    )}
                    {giftSaleErr && (
                      <p className="promo-code-error">{giftSaleErr}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="sub-total-box">
              <div
                className={
                  isPaymentIntentStart
                    ? "user-detail-main-box scroll-wrapper custompaymentformbox"
                    : "user-detail-main-box scroll-wrapper"
                }
              >
                {!isPaymentIntentStart && (
                  <>
                    <form
                      onSubmit={handleSubmit(handleApply)}
                      autoComplete="off"
                    >
                      <div className="your-address-box form-section-border">
                        <h4>Check-In Details</h4>
                        <div className="field-box">
                          <label className="label-on-line">Location *</label>
                          <textarea
                            placeholder="Store name or FXRY Studio"
                            maxLength={300}
                            {...register("dropofflocation", {
                              required: true,
                            })}
                          ></textarea>
                        </div>
                        {isStudioIntake && (
                          <div className="field-box">
                            <label className="label-on-line">
                              Visit Type *
                            </label>
                            <select
                              {...register("intake_visit_type", {
                                required: isStudioIntake,
                              })}
                            >
                              <option value="" disabled>
                                Choose
                              </option>
                              <option value="appointment">Appointment</option>
                              <option value="walk-in">Walk-in</option>
                            </select>
                            {errors.intake_visit_type && (
                              <p role="alert">Select the visit type</p>
                            )}
                          </div>
                        )}
                        <div className="field-box">
                          <label className="label-on-line">
                            Checked In By *
                          </label>
                          <select
                            {...register("intake_person", {
                              required: true,
                            })}
                          >
                            <option value="" disabled>
                              Choose
                            </option>
                            {INTAKE_PEOPLE.map((person) => (
                              <option key={person} value={person}>
                                {person}
                              </option>
                            ))}
                          </select>
                          {errors.intake_person && (
                            <p role="alert">Select who checked this order in</p>
                          )}
                        </div>
                        <p
                          className={`para click-to-change ${
                            wantsRush ? "subscribed" : ""
                          }`}
                          onClick={() => setWantsRush(!wantsRush)}
                          style={{ marginTop: "0.75rem" }}
                        >
                          <span>
                            <input
                              type="checkbox"
                              checked={wantsRush}
                              onChange={(e) => setWantsRush(e.target.checked)}
                            />
                          </span>
                          Rush service (customer paid to move up the due date)
                        </p>
                        {wantsRush && (
                          <>
                            <div className="field-box">
                              <label className="label-on-line">
                                New Due Date*
                              </label>
                              <input
                                type="date"
                                {...register("rushDueDate", {
                                  required: wantsRush,
                                })}
                              />
                              {errors.rushDueDate && (
                                <p role="alert">Select the new due date</p>
                              )}
                            </div>
                            {rushGarments.length > 1 && (
                              <div className="field-box rush-pieces">
                                <label className="label-on-line">
                                  Which pieces are rushed?*
                                </label>
                                {rushGarments.map((itm) => (
                                  <label
                                    key={itm.id}
                                    className="rush-pieces__row"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!notRushedIds.has(itm.id)}
                                      onChange={() => toggleRushGarment(itm.id)}
                                    />
                                    <span>
                                      {[itm.name, itm.color, itm.brand]
                                        .filter(Boolean)
                                        .join(" · ") || "Item"}
                                      {itm.description && (
                                        <small> {itm.description}</small>
                                      )}
                                    </span>
                                  </label>
                                ))}
                                <p className="rush-pieces__note">
                                  {rushedCount === rushGarments.length
                                    ? "Whole order rushed."
                                    : `${rushedCount} of ${rushGarments.length} rushed. The rest keep the normal due date.`}
                                </p>
                              </div>
                            )}
                            <div className="field-box">
                              <label className="label-on-line">
                                Rush Fee ($)*
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="e.g. 20"
                                value={rushFeeInput}
                                onChange={(e) =>
                                  setRushFeeInput(e.target.value)
                                }
                              />
                            </div>
                          </>
                        )}
                        {linesNeedingReason(cartItems?.[0]?.services).map(
                          ({ service, index }) => {
                            const key = customLineKey(service, index);
                            return (
                              <div className="field-box" key={key}>
                                <label className="label-on-line">
                                  Reason for custom price: {service?.name}*
                                </label>
                                <input
                                  type="text"
                                  maxLength={200}
                                  placeholder="e.g. pocket bag torn through"
                                  value={customReasons[key] || ""}
                                  onChange={(e) =>
                                    setCustomReasons((prev) => ({
                                      ...prev,
                                      [key]: e.target.value,
                                    }))
                                  }
                                />
                              </div>
                            );
                          },
                        )}
                        {deliveryType !== "boutique" && (
                          <p
                            className={`para click-to-change ${
                              waivePickup ? "subscribed" : ""
                            }`}
                            onClick={() => setWaivePickup(!waivePickup)}
                          >
                            <span>
                              <input
                                type="checkbox"
                                checked={waivePickup}
                                onChange={(e) =>
                                  setWaivePickup(e.target.checked)
                                }
                              />
                            </span>
                            Waive pick-up fee
                          </p>
                        )}
                        <p
                          className={`para click-to-change ${
                            wantsMailBack ? "subscribed" : ""
                          }`}
                          onClick={() => setWantsMailBack(!wantsMailBack)}
                          style={{ marginTop: "0.75rem" }}
                        >
                          <span>
                            <input
                              type="checkbox"
                              checked={wantsMailBack}
                              onChange={(e) =>
                                setWantsMailBack(e.target.checked)
                              }
                            />
                          </span>
                          Mail this back instead of studio pickup
                          {shipBackFee != null
                            ? ` ($${Number(shipBackFee).toFixed(2)})`
                            : ""}
                        </p>
                        {wantsMailBack && shipBackFeeStatus !== "ready" && (
                          <p className="para" role="alert">
                            {shipBackFeeStatus === "loading"
                              ? "Loading the ship-back fee."
                              : "Couldn't load the ship-back fee."}{" "}
                            {shipBackFeeStatus === "error" && (
                              <button
                                type="button"
                                className="text-black"
                                onClick={() =>
                                  setShipBackFeeRetry((n) => n + 1)
                                }
                              >
                                Try again
                              </button>
                            )}
                          </p>
                        )}
                        {wantsMailBack && (
                          <p
                            className={`para click-to-change ${
                              waiveShipBack ? "subscribed" : ""
                            }`}
                            onClick={() => setWaiveShipBack(!waiveShipBack)}
                          >
                            <span>
                              <input
                                type="checkbox"
                                checked={waiveShipBack}
                                onChange={(e) =>
                                  setWaiveShipBack(e.target.checked)
                                }
                              />
                            </span>
                            Waive ship-back fee
                          </p>
                        )}
                        {((wantsMailBack && waiveShipBack) ||
                          (deliveryType !== "boutique" && waivePickup)) && (
                          <div className="field-box">
                            <label className="label-on-line">
                              Reason for waiving the fee*
                            </label>
                            <input
                              type="text"
                              maxLength={200}
                              placeholder="e.g. our mistake last time"
                              value={feeWaiverReason}
                              onChange={(e) =>
                                setFeeWaiverReason(e.target.value)
                              }
                            />
                          </div>
                        )}
                        {wantsMailBack && (
                          <>
                            <div className="field-box">
                              <label className="label-on-line">
                                Mailing Address*
                              </label>
                              <input
                                type="text"
                                placeholder="Street address"
                                {...register("addressText", {
                                  required: wantsMailBack,
                                })}
                              />
                              {errors.addressText && (
                                <p role="alert">Mailing address is required</p>
                              )}
                            </div>
                            <div className="field-box">
                              <label className="label-on-line">Line 2</label>
                              <input
                                type="text"
                                placeholder="Apartment, suite, unit, etc. (optional)"
                                {...register("line2")}
                              />
                            </div>
                            <div className="field-box">
                              <label className="label-on-line">City*</label>
                              <input
                                type="text"
                                {...register("city", {
                                  required: wantsMailBack,
                                })}
                              />
                              {errors.city && (
                                <p role="alert">City is required</p>
                              )}
                            </div>
                            <div className="field-box">
                              <label className="label-on-line">State*</label>
                              <input
                                type="text"
                                maxLength={2}
                                style={{ textTransform: "uppercase" }}
                                {...register("state", {
                                  required: wantsMailBack,
                                })}
                                onChange={(e) =>
                                  setValue(
                                    "state",
                                    e.target.value.toUpperCase(),
                                  )
                                }
                              />
                              {errors.state && (
                                <p role="alert">State is required</p>
                              )}
                            </div>
                            <div className="field-box">
                              <label className="label-on-line">Zip*</label>
                              <input
                                type="text"
                                maxLength={10}
                                {...register("code", {
                                  required: wantsMailBack,
                                })}
                              />
                              {errors.code && (
                                <p role="alert">Zip code is required</p>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                      <div
                        className="personal-detail-box form-section-border"
                        ref={personalDetailRef}
                      >
                        <h4>Personal Details</h4>
                        {appointmentPrefill && (
                          <p className="para appointment-prefill-note">
                            Filled in from {appointmentPrefill.name || "their"}
                            {appointmentPrefill.name ? "’s" : ""} appointment.
                            Check the spelling with them.{" "}
                            {!lookupAnyway && !lookedUp && (
                              <button
                                type="button"
                                className="customer-lookup-clear"
                                onClick={() => setLookupAnyway(true)}
                              >
                                Not matching their usual details?
                              </button>
                            )}
                          </p>
                        )}
                        {(!appointmentPrefill || lookupAnyway || lookedUp) && (
                          <CustomerLookup
                            filledFrom={lookedUp}
                            onClear={() => {
                              setLookedUp(null);
                              setLookupAnyway(false);
                              // On an appointment order, put the booking's own
                              // details back rather than emptying the form --
                              // they are what the customer actually typed.
                              const back = appointmentPrefill || {};
                              for (const field of [
                                "firstName",
                                "lastName",
                                "email",
                                "number",
                              ]) {
                                setValue(field, back[field] || "", {
                                  shouldValidate: false,
                                });
                              }
                            }}
                            onPick={(customer) => {
                              setValue("firstName", customer.firstName, {
                                shouldValidate: true,
                              });
                              setValue("lastName", customer.lastName, {
                                shouldValidate: true,
                              });
                              setValue("email", customer.email, {
                                shouldValidate: true,
                              });
                              setValue("number", customer.phone, {
                                shouldValidate: true,
                              });
                              setLookedUp(customer.firstName || customer.name);
                            }}
                          />
                        )}
                        <div className="field-box">
                          <label className="label-on-line">First Name*</label>
                          <input
                            type="text"
                            {...register("firstName", {
                              required: true,
                              maxLength: 50,
                            })}
                            onChange={(e) =>
                              inputChangehandler({ type: "personalInfo", e })
                            }
                          />
                          {errors.firstName?.type === "required" && (
                            <p role="alert">First name is required</p>
                          )}
                        </div>
                        <div className="field-box">
                          <label className="label-on-line">Last Name*</label>
                          <input
                            type="text"
                            {...register("lastName", {
                              required: true,
                              maxLength: 50,
                            })}
                            onChange={(e) =>
                              inputChangehandler({ type: "personalInfo", e })
                            }
                          />
                          {errors.lastName?.type === "required" && (
                            <p role="alert">Last name is required</p>
                          )}
                        </div>
                        <div className="field-box">
                          <label className="label-on-line">Email*</label>
                          <input
                            type="email"
                            {...register("email", {
                              required: true,
                              pattern: {
                                value:
                                  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                                message: "Please enter a valid email address",
                              },
                              maxLength: {
                                value: 100,
                                message:
                                  "Email should not exceed 100 characters",
                              },
                            })}
                            onChange={(e) =>
                              inputChangehandler({ type: "personalInfo", e })
                            }
                          />
                          {errors.email && <p>{errors.email.message}</p>}
                        </div>
                        <p
                          className={`para click-to-change ${
                            subscribed ? "subscribed" : ""
                          }`}
                          onClick={handleSubscriptionClick}
                        >
                          <span>
                            {/* Mirrors `subscribed`, which is what gets saved. It
                                used to start ticked while `subscribed` started
                                false, so the box showed the opposite choice. */}
                            <input
                              type="checkbox"
                              name="subscribed"
                              checked={subscribed}
                              readOnly
                            />
                          </span>
                          Yes, send me FXRY emails: seasonal repair reminders,
                          studio news and new Second Mark pieces. One or two a
                          month.
                        </p>
                        <div className="field-box">
                          <label className="label-on-line">
                            {" "}
                            Mobile Number*{" "}
                          </label>
                          <input
                            type="tel"
                            inputMode="tel"
                            {...register("number", {
                              required: true,
                              pattern: {
                                value: /^(\(?\d{3}\)?[-. ]?\d{3}[-. ]?\d{4})$/,
                                message: "Please enter a valid US phone number",
                              },
                            })}
                          />
                        </div>
                        {errors.number && (
                          <p className="error-message">
                            {errors.number.message}
                          </p>
                        )}
                        {/* Texting consent (fxrybackend helper/smsConsent.js).
                            Starts unticked and is separate from the newsletter
                            and the terms on purpose: US carriers only approve
                            FXRY's texting when the customer ticks it themselves.
                            Keep the words in step with SMS_CONSENT_TEXT. */}
                        <p
                          className={`para click-to-change ${smsConsent ? "subscribed" : ""}`}
                          onClick={() => setSmsConsent(!smsConsent)}
                        >
                          <span>
                            <input
                              type="checkbox"
                              name="smsConsent"
                              checked={smsConsent}
                              readOnly
                            />
                          </span>
                          {/* One span: the paragraph is display:flex, so bare
                              text and <strong> became separate squeezed columns.
                              The inline style undoes .click-to-change span
                              (display:block keeps the policy links inline). */}
                          <span
                            style={{
                              display: "block",
                              height: "auto",
                              padding: 0,
                              margin: 0,
                              flex: 1,
                            }}
                          >
                            <strong>Ask the customer:</strong> Text me about my
                            appointment, order or invoice from FXRY. Message
                            frequency varies. Msg &amp; data rates may apply.
                            Reply HELP for help, STOP to opt out.{" "}
                            <a
                              className="text-black"
                              target="blank"
                              href="https://www.thefxry.com/terms-and-conditions"
                              style={{
                                fontSize: "inherit",
                                fontFamily: "inherit",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Terms
                            </a>{" "}
                            and{" "}
                            <a
                              className="text-black"
                              target="blank"
                              href="https://www.thefxry.com/privacy-policy"
                              style={{
                                fontSize: "inherit",
                                fontFamily: "inherit",
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Privacy Policy
                            </a>
                            .
                          </span>
                        </p>
                        <p className="smallest-para">
                          By continuing, you agree to FXRY's{" "}
                          <a
                            className="text-black"
                            target="blank"
                            href="https://www.thefxry.com/terms-and-conditions"
                          >
                            Terms and Conditions.
                          </a>
                        </p>
                      </div>
                      {/* <div className="your-address-box form-section-border">
                        <h4>
                          Delivery Address Type{" "}
                          <span style={{ color: "red" }}>*</span>
                        </h4>
                        <select
                          {...register("delivery_address_type")}
                          onChange={(e) => setDeliveryType(e.target.value)}
                          style={{
                            width: "100%",
                            padding: "0.5rem",
                            borderRadius: "0.2rem",
                          }}
                          disabled
                        >
                          <option value={null}>Choose</option>
                          <option value={"boutique"} selected>
                          Store name or FXRY Studio
                          </option>
                          <option value={"popup"}>Deliver to me</option>
                        </select>
                      </div> */}
                      {deliveryTypeError != null && (
                        <p role="alert">{deliveryTypeError}</p>
                      )}
                      <div className="your-address-box form-section-border">
                        <h4>How Did You Hear About Us ? *</h4>
                        <select
                          {...register("hear_about_us", {
                            required: true,
                          })}
                        >
                          <option value="" disabled>
                            Choose one
                          </option>
                          {HEAR_ABOUT_US_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        {errors.hear_about_us && (
                          <p role="alert">
                            Ask the customer how they heard about us
                          </p>
                        )}
                        {bookedHearAboutUs(appointmentPrefill?.hearAboutUs) &&
                          watch("hear_about_us") ===
                            bookedHearAboutUs(appointmentPrefill.hearAboutUs)
                              .hear_about_us && (
                            <p
                              style={{
                                marginTop: "0.4rem",
                                fontSize: "0.9em",
                                color: "#4f514d",
                              }}
                            >
                              From their booking. No need to ask.
                            </p>
                          )}
                        {watch("hear_about_us") === "Local Shop Referral" && (
                          <div
                            className="field-box"
                            style={{ marginTop: "0.5rem" }}
                          >
                            <input
                              type="text"
                              placeholder="Which shop? (optional)"
                              maxLength={100}
                              {...register("local_shop_name")}
                            />
                          </div>
                        )}
                      </div>
                      <div className="payment-info-box" ref={paymentDetailRef}>
                        <div className="paypal-detail-box"></div>
                        {!(isComplimentary || giftCoversAll) && (
                          <div className="payment-mode-box">
                            <h4>Payment Method</h4>
                            <div
                              className="payment-mode-toggle"
                              role="radiogroup"
                              aria-label="Payment method"
                            >
                              <label
                                className={
                                  paymentMode === "terminal"
                                    ? "payment-mode-option payment-mode-option--selected"
                                    : "payment-mode-option"
                                }
                              >
                                <input
                                  type="radio"
                                  name="paymentMode"
                                  value="terminal"
                                  checked={paymentMode === "terminal"}
                                  onChange={() => setPaymentMode("terminal")}
                                />
                                Tap to Pay
                              </label>
                              <label
                                className={
                                  paymentMode === "card"
                                    ? "payment-mode-option payment-mode-option--selected"
                                    : "payment-mode-option"
                                }
                              >
                                <input
                                  type="radio"
                                  name="paymentMode"
                                  value="card"
                                  checked={paymentMode === "card"}
                                  onChange={() => setPaymentMode("card")}
                                />
                                Manual Credit Card Entry
                              </label>
                            </div>
                          </div>
                        )}
                        <button
                          className="section-main-btn"
                          type="submit"
                          disabled={
                            submitting ||
                            (!isComplimentary && taxStatus !== "ready") ||
                            reasonsMissing.length > 0 ||
                            shipBackFeeMissing
                          }
                        >
                          {submitting
                            ? "Starting…"
                            : !isComplimentary && taxStatus === "loading"
                              ? "Calculating tax…"
                              : isComplimentary
                                ? "Place Order"
                                : giftCoversAll
                                  ? "Place order (paid by gift card)"
                                  : "Proceed To Payment"}
                          <span>
                            <img src={btnArrow} alt="" />
                          </span>
                        </button>
                        {reasonsMissing.length > 0 && (
                          <p className="para" role="alert">
                            {reasonsMissing[0]}
                          </p>
                        )}
                        {!isComplimentary && taxStatus === "error" && (
                          <p className="para" role="alert">
                            {serverOn
                              ? "We couldn't get the price, so payment can't start yet."
                              : "Tax couldn't be calculated, so payment can't start yet."}{" "}
                            <button
                              type="button"
                              className="text-black"
                              onClick={() => setTaxRetry((n) => n + 1)}
                            >
                              Try again
                            </button>
                          </p>
                        )}
                      </div>
                      {deliveryTypeError != null && (
                        <p role="alert">{deliveryTypeError}</p>
                      )}
                      {collectionDateError && (
                        <p
                          className="para"
                          style={{
                            fontWeight: "bold",
                            fontSize: "0.8rem",
                            color: "black",
                          }}
                        >
                          {collectionDateError}
                        </p>
                      )}
                    </form>
                  </>
                )}
                {isPaymentIntentStart && paymentMode === "terminal" && (
                  <TerminalCheckoutForm
                    orderData={postDataToApi}
                    amount={chargedWithGiftSale(payCents, giftSale)}
                    name={postDataToApi?.[0]?.user?.personalInfo?.fullName}
                    email={postDataToApi?.[0]?.user?.personalInfo?.email}
                    taxCalculationId={stripeTaxCalculationId || undefined}
                    giftSaleCertificateId={giftSale?.id || null}
                    onGiftRefused={async (message) => {
                      // Nothing was charged: back to the form, the code
                      // dropped if it no longer covers this order.
                      setIsPaymentIntentStart(false);
                      setPostDataToApi(null);
                      await handleGiftRefusal(message);
                    }}
                    onCancel={() => {
                      // They changed their mind at the reader. The payment is
                      // already cancelled at Stripe; this just puts the form
                      // back, cart and customer intact, so staff can change
                      // the order or let them leave.
                      setIsPaymentIntentStart(false);
                      setPostDataToApi(null);
                      toast("Payment cancelled. Nothing was charged.");
                    }}
                    onPricingRefused={handlePricingRefusal}
                    onSuccess={(orderId) => {
                      sessionStorage.setItem("orderId", orderId);
                      sessionStorage.setItem("thankyoupage", "yes");
                      navigate("/thankyou");
                    }}
                  />
                )}
                {isPaymentIntentStart && paymentMode === "card" && (
                  <>
                    <Elements options={options} stripe={stripePromise}>
                      <CheckoutForm
                        dpmCheckerLink={dpmCheckerLink}
                        orderData={postDataToApi}
                        amount={chargedWithGiftSale(payCents, giftSale) / 100}
                      />
                    </Elements>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Checkout;
